/**
 * School Orders — the SCHOOL_ADMIN bulk-ordering surface for
 * /orders/school.
 *
 * This page is the shell: it resolves the viewer's school(s), handles the
 * empty-state branching (no school memberships / no grade ranges), gates on
 * the org's SCHOOL_ORDER channel, and hands off to `WeekOrdersView` — the
 * consolidated weekly view (expandable day cards + a right rail with the month
 * calendar, order deadline, and cart). The week stepper that drives the
 * selected week lives in the page header (beside "Orders"); its state is owned
 * here so the header and the day list stay in sync.
 *
 * Rendered full-bleed (like the kitchen surface) so the header (with the week
 * stepper) stays fixed while the day-card list scrolls. Review & checkout is a
 * dedicated full-page route (`/orders/school/checkout`).
 */

import { cn } from '@ordo/ui/cn'
import { EmptyState } from '@ordo/ui/empty-state'
import { Skeleton } from '@ordo/ui/skeleton'
import { Calendar, School } from 'lucide-react'
import { type ReactNode, useMemo, useState } from 'react'
import { useLocation } from 'react-router'

import { isChannelEnabled, useOrgOrderingConfigQuery } from '@/api/org-ordering-config'
import { useHasSchoolOrg } from '@/api/school-onboarding'
import { type AdminSchool, useMySchoolAdminLocationsQuery } from '@/api/school-orders'
import { GradeRangesEmptyState, resolveEmptyStateAudience } from '@/components/orders/school/grade-ranges-empty-state'
import { OrderGroupsEmptyState } from '@/components/orders/school/order-groups-empty-state'
import { SchoolOrderActions } from '@/components/orders/school/school-order-actions'
import { WeekNav } from '@/components/orders/school/week-nav'
import { WeekOrdersView } from '@/components/orders/school/week-orders-view'
import { OrdoPageHeader } from '@/components/ordo-page-header'
import { addDaysKey, mondayOf, todayKey } from '@/lib/school-orders/calendar'
import { hasOrderColumns, orderColumnsGroupBy } from '@/lib/school-orders/columns'
import { acceptsSchoolOrders, orderableCampuses } from '@/lib/school-orders/orderable-campuses'
import { resolveSelectedSchool } from '@/lib/school-orders-selection'
import { useIsMobile } from '@/lib/use-is-mobile'
import { useProfile } from '@/stores/profile'

/**
 * Full-height shell: the Orders title header + switcher, then a controls row
 * (week nav on the left, location selector — and on desktop the quick-action
 * chips — on the right), then the content fills the rest. Stacking the controls
 * under the title keeps the row readable on mobile instead of cramming the week
 * nav and the location selector onto the title line; the row wraps on very
 * narrow screens so the right-hand group drops below the week nav instead of
 * overflowing.
 *
 * The row matches the parent order surface (`FamilyWeekOrdersPage`): on mobile
 * it bleeds to the screen edges and, once the day list scrolls, its bottom
 * border fades in while the padding tightens — so the week stepper reads as a
 * sticky toolbar rather than page content.
 */
function Shell({
  switcher,
  controls,
  scrolled,
  children,
}: {
  switcher?: ReactNode
  controls?: ReactNode
  /** Day list is scrolled away from the top — compact the mobile controls row. */
  scrolled?: boolean
  children: ReactNode
}) {
  return (
    <div className="mx-auto flex h-full w-full min-h-0 max-w-[100rem] flex-col px-4">
      <div className="md:pt-4 lg:pt-5">
        <OrdoPageHeader icon={Calendar} title="Orders" className="mb-0" />
        {switcher}
      </div>
      {controls ? (
        <div
          className={cn(
            '-mx-4 flex flex-wrap items-center justify-between gap-3 gap-y-1 border-b px-4 transition-[padding,border-color] duration-200 md:mx-0 md:border-0 md:px-0 md:py-0 md:pt-3 lg:pt-4',
            scrolled ? 'border-border py-2' : 'border-transparent py-5',
          )}>
          {controls}
        </div>
      ) : null}
      {children}
    </div>
  )
}

export function SchoolOrdersView({ switcher }: { switcher?: ReactNode }) {
  const schoolsQuery = useMySchoolAdminLocationsQuery()
  const schoolOrg = useHasSchoolOrg()
  const location = useLocation()
  const { profile } = useProfile()
  // Below `lg` the quick actions live in the day list's "Options" menu instead
  // of this row (PROD-299), so only one of the two ever mounts.
  const isMobile = useIsMobile()
  // Compact the controls row once the day list scrolls (parent-order parity).
  const [listScrolled, setListScrolled] = useState(false)
  // Campuses with nothing published yet are dropped here, once, so the
  // switcher, the day panel's location strip and the cart rail all agree.
  // Setup surfaces read the same query WITHOUT this filter — see
  // `orderableCampuses`.
  const schools = useMemo<AdminSchool[]>(
    () => orderableCampuses(schoolsQuery.data?.me?.schoolAdminLocations ?? []),
    [schoolsQuery.data],
  )
  const requestedLocationId = useMemo(() => new URLSearchParams(location.search).get('locationId'), [location.search])
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null)
  const school = useMemo<AdminSchool | null>(
    () => resolveSelectedSchool(schools, selectedLocationId ?? requestedLocationId),
    [requestedLocationId, schools, selectedLocationId],
  )
  // Read-only for either reason the school can't order: the org's
  // SCHOOL_ORDER channel is off, or this campus orders through parents only
  // (FACTORY-553 — shown with its parent demand rather than hidden). Decided
  // here, once, because two places consume it: the week view, and the
  // controls row's quick actions, whose Field Trip and Supplies & Back Up
  // entries place school orders (FACTORY-554).
  const orgConfigQuery = useOrgOrderingConfigQuery(school?.organizationId ?? null)
  const schoolOrderEnabled = isChannelEnabled(orgConfigQuery.data ?? null, 'SCHOOL_ORDER')
  const readOnly = !schoolOrderEnabled || (school !== null && !acceptsSchoolOrders(school))

  // The selected week lives here so the week stepper can sit in the page header
  // (beside "Orders") while `WeekOrdersView` renders the days for that week.
  // Defaults to the current service week + 1 — the soonest week schools
  // typically order. On weekends the current service week is already the
  // upcoming one (its order window has closed), so the default lands on the
  // first week whose window is realistically open.
  const [anchorKey, setAnchorKey] = useState(() => addDaysKey(mondayOf(todayKey()), 7))
  const selectWeek = (mondayKey: string) => setAnchorKey(mondayOf(mondayKey))

  let content: ReactNode = null
  if (schoolsQuery.isPending && !schoolsQuery.data) {
    content = <ViewLoading />
  } else if (schoolsQuery.error) {
    content = <ErrorPanel message={schoolsQuery.error.message} />
  } else if (schools.length === 0) {
    // Empty state branching — membership count + profile-code resolution.
    const profileCodes = profile?.profileCodes ?? []
    const hasSchoolAdminProfile = profileCodes.includes('SCHOOL_ADMIN') || profile?.isOrdoStaff === true
    const audience = resolveEmptyStateAudience({
      hasSchoolAdminProfile,
      schoolMembershipCount: 0,
      hasSchoolOrg: schoolOrg.hasSchoolOrg,
    })
    content =
      audience === 'not-school-admin' ? (
        <EmptyState
          icon={<School className="h-12 w-12" />}
          title="No school admin memberships"
          description="School pre-orders show up here once you're added as an admin to a school."
        />
      ) : (
        <GradeRangesEmptyState audience={audience} schoolName={schoolOrg.schoolName} />
      )
  } else if (school && !hasOrderColumns(school)) {
    // No order columns to order against — AGE_GROUP campuses need age
    // groups, every other order dimension needs configured order groups.
    // Always name the school here: with the header dropdown gone, this is
    // the only cue for WHICH campus lacks configuration.
    content = (
      <div className="flex flex-col gap-6 pt-4">
        <h2 className="text-lg font-medium text-foreground">{school.name}</h2>
        {orderColumnsGroupBy(school) !== 'AGE_GROUP' ? (
          <OrderGroupsEmptyState />
        ) : (
          <GradeRangesEmptyState audience="has-school-membership" />
        )}
      </div>
    )
  } else if (school) {
    content = (
      <SchoolOrdersBody
        school={school}
        schools={schools}
        onSelectSchool={setSelectedLocationId}
        anchorKey={anchorKey}
        onAnchorChange={selectWeek}
        readOnly={readOnly}
        onScrolledChange={setListScrolled}
      />
    )
  }

  // Controls row beneath the title: week nav on the left; on the right, the
  // quick-action chips (desktop only — on mobile they live in the day list's
  // "Options" menu). Campus switching lives in the day panel's location tab
  // strip (multi-campus admins), not in a header dropdown. `ml-auto` keeps
  // the right-hand group right-aligned even when the row wraps on narrow
  // screens.
  const controls =
    school && hasOrderColumns(school) ? (
      <>
        {/* The stepper's prev button is a 32px hit-target around a 20px glyph,
            so its ink sits ~6px inside the page gutter that the day cards, the
            Options heading and the cart all share. Pull the group back by that
            slack on mobile so the chevron aligns with them optically. Done here
            rather than in `WeekNav` — the parent order page shares it. */}
        <div className="max-lg:-ml-1.5">
          <WeekNav anchorKey={anchorKey} onAnchorChange={selectWeek} />
        </div>
        <div className="ml-auto flex items-center gap-2">
          {isMobile ? null : <SchoolOrderActions school={school} schools={schools} readOnly={readOnly} />}
        </div>
      </>
    ) : null

  return (
    <Shell switcher={switcher} controls={controls} scrolled={listScrolled}>
      {content}
    </Shell>
  )
}

/**
 * The resolved-school body: fills the remaining height with the week view.
 * The location picker, the week stepper and the read-only decision all live
 * in `SchoolOrdersView`.
 */
function SchoolOrdersBody({
  school,
  schools,
  onSelectSchool,
  anchorKey,
  onAnchorChange,
  readOnly,
  onScrolledChange,
}: {
  school: AdminSchool
  /** All the admin's schools — feeds the day panel's location tab strip. */
  schools: AdminSchool[]
  /** Select another campus from the day panel's location tab strip. */
  onSelectSchool: (locationId: string) => void
  anchorKey: string
  onAnchorChange: (mondayKey: string) => void
  /** The school can't order here (org channel off, or a PARENT-only campus). */
  readOnly: boolean
  /** Fires when the day list starts/stops being scrolled away from the top. */
  onScrolledChange?: (scrolled: boolean) => void
}) {
  return (
    // `md:pt-3`, not `pt-3` — on mobile the controls row above already carries
    // `py-5`, so an unconditional top padding stacked two gaps and left the week
    // label floating. Matches the parent order page.
    <div className="flex min-h-0 flex-1 flex-col gap-3 md:pt-3">
      <WeekOrdersView
        school={school}
        schools={schools}
        onSelectSchool={onSelectSchool}
        anchorKey={anchorKey}
        onAnchorChange={onAnchorChange}
        readOnly={readOnly}
        onScrolledChange={onScrolledChange}
      />
    </div>
  )
}

function ViewLoading() {
  return (
    <div className="flex flex-col gap-3 pt-6">
      <Skeleton className="h-9 w-40" />
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-20 w-full rounded-xl" />
      ))}
    </div>
  )
}

function ErrorPanel({ message }: { message: string }) {
  return (
    <div className="mt-6 rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
      Couldn't load school orders. {message}
    </div>
  )
}
